import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  TextField,
  Tooltip,
  Button,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  Collapse,
  Divider,
  useTheme,
} from "@mui/material";
import {
  Sync as SyncIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useAppSelector, useAppDispatch } from "../../hooks/hooks";
import {
  deleteNFTCollection,
  updateNFTCollection,
  addNftCollection,
  addNewRow,
  fetchNftsForSelection,
  fetchAllMyNfts,
  addUserNft,
  removeUserNft,
} from "../../redux/nftSlice";
import { NFTCollection } from "../../models/NFTCollection";

const NFTCollectionsSection = ({ isMobile = false }) => {
  const theme = useTheme();
  const { collections } = useAppSelector((state) => state.nft);
  const dispatch = useAppDispatch();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<NFTCollection>>({});
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const { nftsForSelection, allMyNfts, loading } = useAppSelector(
    (state) => state.nft
  );
  const selectedNftIds = new Set(allMyNfts.map((nft) => nft.identifier));

  const handleEdit = (collection: NFTCollection) => {
    setEditingId(collection.id);
    setEditData({
      collectionName: collection.collectionName,
      collectionAddress: collection.collectionAddress,
    });
  };

  const handleSave = async (id: string) => {
    if (id === "0") {
      await dispatch(
        addNftCollection({
          collectionName: editData.collectionName || "",
          collectionAddress: editData.collectionAddress || "",
        })
      );
    } else {
      await dispatch(
        updateNFTCollection({
          id,
          ...editData,
          collectionName: editData.collectionName || "",
          collectionAddress: editData.collectionAddress || "",
        })
      );
    }
    setEditingId(null);
    setEditData({});
  };

  const handleSelect = (nftId: string) => {
    console.log("Selected NFT ID:", nftId);
    console.log("All My NFTs Find:", selectedNftIds.has(nftId) ? "Yes" : "No");
    try {
      if (selectedNftIds.has(nftId)) {
        const nft: any = allMyNfts.find((nft) => nft.identifier === nftId);
        dispatch(removeUserNft({ nftId: nft.id })); // Deselect the NFT if already selected
      } else {
        if (nftsForSelection) {
          const nft = nftsForSelection.find((nft) => nft.identifier === nftId);
          dispatch(
            addUserNft({
              nft,
            })
          );
        }
      }
    } catch (error) {
      console.error("Error handling NFT selection:", error);
    }

    // setSelectedNfts((prev) =>
    //   prev.includes(nftId)
    //     ? prev.filter((id) => id !== nftId)
    //     : [...prev, nftId]
    // );
  };

  const handleAdd = () => {
    dispatch(
      addNewRow({
        collectionAddress: "",
        collectionName: "",
        imported: 0,
        id: "0",
      })
    );
    setEditingId("0");
    setEditData({
      collectionName: "",
      collectionAddress: "",
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleChange = (field: keyof NFTCollection, value: string) => {
    setEditData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDelete = async (id: string) => {
    await dispatch(deleteNFTCollection({ id }));
  };

  const handleSync = async (id: string) => {
    await dispatch(fetchNftsForSelection({ id }));
  };

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const scrollableBoxStyles = {
    maxHeight: "40vh",
    overflowY: "auto",
    scrollbarWidth: "none", // For Firefox
    "&::-webkit-scrollbar": {
      display: "none", // For Chrome, Safari, and Opera
    },
  };

  return (
    <Paper
      sx={{
        p: 2,
        width: "100%",
        maxWidth: isMobile ? "100%" : "800px",
        margin: "auto",
        borderRadius: isMobile ? 0 : 2,
        boxShadow: isMobile ? "none" : 3,
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Typography variant="h6">My Collections</Typography>
        <Button onClick={handleAdd} variant="contained" size="small">
          Add New
        </Button>
      </Box>

      <Grid container spacing={2}>
        {collections.map((collection) => (
          <Grid key={collection.id}>
            <Card variant="outlined">
              <CardContent>
                {editingId === collection.id ? (
                  <Box>
                    <TextField
                      label="Collection Name"
                      value={editData.collectionName || ""}
                      onChange={(e) =>
                        handleChange("collectionName", e.target.value)
                      }
                      fullWidth
                      margin="dense"
                    />
                    <TextField
                      label="Collection Address"
                      value={editData.collectionAddress || ""}
                      onChange={(e) =>
                        handleChange("collectionAddress", e.target.value)
                      }
                      fullWidth
                      margin="dense"
                    />
                  </Box>
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {collection.collectionName}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontFamily="monospace"
                      >
                        {isMobile
                          ? `${collection.collectionAddress?.slice(
                              0,
                              6
                            )}...${collection.collectionAddress?.slice(-4)}`
                          : `${collection.collectionAddress?.slice(
                              0,
                              6
                            )}...${collection.collectionAddress?.slice(-4)}`}
                      </Typography>
                      <Chip
                        label={`${
                          collection.imported ? collection.imported : 0
                        } items`}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                    <IconButton
                      onClick={async () => {
                        await dispatch(fetchAllMyNfts());
                        await dispatch(
                          fetchNftsForSelection({ id: collection.id })
                        );
                        toggleExpand(collection.id);
                      }}
                    >
                      {expandedId === collection.id ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Box>
                )}
              </CardContent>

              <CardActions sx={{ justifyContent: "space-between" }}>
                {editingId === collection.id ? (
                  <>
                    <Box>
                      <Tooltip title="Save">
                        <IconButton onClick={() => handleSave(collection.id)}>
                          <CheckIcon color="success" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton onClick={handleCancel}>
                          <CloseIcon color="error" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </>
                ) : (
                  <Box>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleEdit(collection)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sync">
                      <IconButton onClick={() => handleSync(collection.id)}>
                        <SyncIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDelete(collection.id)}>
                        <DeleteIcon color="error" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </CardActions>

              <Collapse
                in={expandedId === collection.id && !editingId}
                timeout="auto"
                unmountOnExit
              >
                <Divider />
                <CardContent>
                  {nftsForSelection && nftsForSelection.length > 0 && (
                    <Box mt={2} sx={scrollableBoxStyles}>
                      <Typography variant="subtitle2" gutterBottom>
                        NFTs in this collection
                      </Typography>
                      <Grid container spacing={1}>
                        {nftsForSelection.map((nft) => (
                          <Grid key={nft.identifier}>
                            <Card
                              variant="outlined"
                              sx={{
                                position: "relative",
                                border: selectedNftIds.has(nft.identifier)
                                  ? `2px solid ${theme.palette.primary.main}`
                                  : undefined,
                              }}
                            >
                              {nft.displayImageUrl ? (
                                <img
                                  src={nft.displayImageUrl}
                                  alt={nft.name}
                                  style={{
                                    width: "100%",
                                    height: "120px",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  justifyContent="center"
                                  height="120px"
                                  bgcolor="action.hover"
                                >
                                  <ImageIcon color="disabled" />
                                </Box>
                              )}
                              <CardContent sx={{ p: 1 }}>
                                <Typography variant="body2" noWrap>
                                  {nft.name}
                                </Typography>
                              </CardContent>
                              <IconButton
                                size="small"
                                sx={{
                                  position: "absolute",
                                  top: 4,
                                  right: 4,
                                  backgroundColor: "background.paper",
                                }}
                                disabled={loading}
                                onClick={() => handleSelect(nft.identifier)}
                              >
                                <CheckIcon
                                  color={
                                    selectedNftIds.has(nft.identifier)
                                      ? "primary"
                                      : "disabled"
                                  }
                                  fontSize="small"
                                />
                              </IconButton>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Collapse>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
};

export default NFTCollectionsSection;
